import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { LanePerformance } from '@/types/generated';
import { formatCount, formatMoney, formatRate } from '@/features/reporting/formatting';

/**
 * The lanes, in full, beside the map.
 *
 * THE TABLE IS NOT THE MAP'S CAPTION — it is the complete answer, and the map is the part of it
 * that could be drawn. A lane whose addresses were never geocoded is still a real corridor with
 * real revenue, so it appears here with a marker saying why it is missing from the picture
 * above. Dropping it from both would quietly understate the business.
 */
export function LaneTable({ lanes, currency }: { lanes: LanePerformance[]; currency: string }) {
  if (lanes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No lanes have been quoted yet.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Lane</TableCell>
            <TableCell align="right">Quotes</TableCell>
            <TableCell align="right">Win rate</TableCell>
            <TableCell align="right">Revenue won</TableCell>
            <TableCell align="right">Margin</TableCell>
            <TableCell align="right">Distance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lanes.map((lane) => {
            const mapped =
              lane.origin_lat !== null &&
              lane.origin_lng !== null &&
              lane.destination_lat !== null &&
              lane.destination_lng !== null;

            return (
              <TableRow key={`${lane.origin}-${lane.destination}`} hover>
                <TableCell>
                  {lane.origin} → {lane.destination}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                    {lane.origin_country}–{lane.destination_country}
                  </Typography>
                  {!mapped && (
                    <Chip size="small" variant="outlined" label="not mapped" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell align="right">{formatCount(lane.quote_count)}</TableCell>
                <TableCell align="right">{formatRate(lane.win_rate_basis_points)}</TableCell>
                <TableCell align="right">{formatMoney(lane.won_value_minor, currency)}</TableCell>
                <TableCell align="right">{formatRate(lane.avg_margin_basis_points)}</TableCell>
                <TableCell align="right">
                  {lane.avg_distance_km === null ? '—' : `${Math.round(lane.avg_distance_km)} km`}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
